import React, { useEffect, useState } from "react";
import { ProgressSpinner } from "primereact/progressspinner";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useFirmDashboardStore } from "../../store/admin/dashboardStore";
import {
  Wallet, Landmark, History, Users, ShoppingBag,
  AlertTriangle, RefreshCcw, MapPin, Truck, TrendingUp,
  Calendar, ArrowUpRight, ArrowDownRight
} from "lucide-react";
import { CurrencyRates, useCurrencyStore } from "../../store/currencyStore";
import { toast } from "react-toastify";

const IconMap: Record<string, any> = {
  Wallet, Landmark, History, Users, ShoppingBag,
  AlertTriangle, MapPin, Truck, TrendingUp
};

const formatCurrency = (value: any) => {
  if (value === null || value === undefined) return "****";
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
  }).format(value);
};

export const FirmDashboard = () => {
  const { loading, error, fetchData, data } = useFirmDashboardStore();
  const { getLiveRates, syncRates } = useCurrencyStore();
  const [rates, setRates] = useState<CurrencyRates | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    loadAllData();
    return () => clearInterval(timer);
  }, []);

  const loadAllData = async () => {
    fetchData();
    const rateData = await getLiveRates();
    setRates(rateData);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await syncRates();
      const updated = await getLiveRates();
      setRates(updated);
      toast.success("Kurlar başarıyla güncellendi!");
    } catch (err) {
      toast.error("Kur güncelleme başarısız.");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return <div className="flex justify-center items-center h-screen bg-[var(--bg)]"><ProgressSpinner /></div>;
  if (error) return <div className="text-red-500 text-center mt-10">{error}</div>;

  return (
    <div className="space-y-8 pb-20 px-2 md:px-4">
      
      {/* --- HEADER: KUR VE ZAMAN (Command Center Style) --- */}
<div className="relative group p-1 select-none">
  {/* ARKA PLAN PARLAMASI */}
  <div 
    className="absolute -inset-1 rounded-[30px] blur-xl opacity-10 group-hover:opacity-30 transition duration-1000" 
    style={{ backgroundColor: 'var(--primary)' }}
  ></div>

  <div className="relative flex justify-between flex-col md:flex-row items-stretch md:items-center gap-4 bg-[var(--card)] backdrop-blur-2xl border border-[var(--border)] shadow-2xl rounded-[26px] p-2 overflow-hidden">
    
    {/* TÜM HEADER BOYUNCA AKAN IŞIK EFEKTİ (Glow Sweep) */}
    <div 
      className="absolute -left-full group-hover:left-[200%] transition-all duration-[1500ms] ease-in-out w-1/2 h-full skew-x-[45deg] opacity-20 pointer-events-none z-20"
      style={{ 
        background: `linear-gradient(to right, transparent, var(--primary), transparent)` 
      }}
    ></div>

    {/* 1. SEKTÖR: KUR ALANI */}
    <div className="relative  flex items-center gap-4 bg-[var(--bg-alt)] rounded-[20px] p-2 pr-4 shadow-inner border border-[var(--border)] overflow-hidden">
      <button onClick={handleSync} disabled={syncing} className="relative z-30 flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-2xl bg-[var(--bg)] transition-all active:scale-90 group/btn border border-[var(--border)]">
        <div className={`absolute inset-0 opacity-20 ${syncing ? 'animate-pulse' : 'hidden'}`} style={{ backgroundColor: 'var(--primary)' }}></div>
        <RefreshCcw size={20} className={`relative z-10 transition-transform duration-700 ${syncing ? 'animate-spin' : 'group-hover/btn:rotate-180'}`} style={{ color: 'var(--primary)' }} />
        <div className="absolute bottom-1 right-1 w-1.5 h-1.5 bg-emerald-500 rounded-full shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
      </button>

      <div className="flex items-center gap-6 overflow-x-auto no-scrollbar scroll-smooth py-1 relative z-10">
        {rates && Object.entries(rates).map(([code, value]) => (
          <div key={code} className="flex flex-col min-w-[90px]">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-[10px] font-black opacity-50 uppercase" style={{ color: 'var(--text)' }}>{code}</span>
              <div className="h-[2px] w-4 opacity-30 rounded-full" style={{ backgroundColor: 'var(--primary)' }}></div>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-lg font-black tracking-tight" style={{ color: 'var(--text)' }}>{value > 0 ? value.toLocaleString('tr-TR', { minimumFractionDigits: 3 }) : "---"}</span>
              <span className="text-[9px] font-bold opacity-40 italic" style={{ color: 'var(--text)' }}>try</span>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* 2. SEKTÖR: ZAMAN KAPSÜLÜ */}
    <div className="relative md:min-w-[320px] flex items-center justify-between bg-[var(--bg)] p-1.5 rounded-[22px] border border-[var(--border)] shadow-lg overflow-hidden">
      <div className="pl-4 pr-6 flex flex-col relative z-10">
        <span className="text-[10px] font-black uppercase tracking-[0.2em] leading-none mb-1.5" style={{ color: 'var(--primary)' }}>{currentTime.toLocaleDateString('tr-TR', { weekday: 'long' })}</span>
        <div className="flex items-center gap-2">
          <Calendar size={12} className="opacity-40" style={{ color: 'var(--text)' }} />
          <span className="text-[10px] font-bold opacity-60 uppercase tracking-widest" style={{ color: 'var(--text)' }}>{currentTime.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
        </div>
      </div>

      <div className="flex-1 bg-[var(--bg-alt)] backdrop-blur-md rounded-[16px] py-2 px-4 flex items-center justify-center border border-[var(--border)] relative overflow-hidden group/time z-10">
        <div className="flex items-baseline gap-1.5 relative z-10">
          <span className="text-3xl font-black tracking-tighter" style={{ color: 'var(--text)' }}>{currentTime.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
          <span className="text-xs font-black animate-pulse w-4 text-center" style={{ color: 'var(--primary)' }}>{currentTime.toLocaleTimeString('tr-TR', { second: '2-digit' })}</span>
        </div>
      </div>
    </div>
  </div>
</div>

      {/* --- WIDGET ALANI --- */}
      {(!data || !data.widgets || data.widgets.length === 0) ? (
        <div className="text-center mt-10 p-10 bg-[var(--card)] border border-[var(--border)] rounded-2xl mx-auto max-w-md">Henüz veri bulunamadı.</div>
      ) : (
      <div className="grid grid-cols-12 gap-6">
        {data.widgets.map((widget: any) => {

          // --- 1. GLASS STATS GRID ---
          if (widget.type === "stats_grid") {
            return (
              <div key={widget.id} className="col-span-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {widget.data.map((stat: any) => {
                  const Icon = IconMap[stat.icon] || ShoppingBag;
                  const isCurrency = ['cash', 'bank', 'monthly_purchase', 'monthly_return', 'monthly_sales', 'monthly_collection'].includes(stat.id);
                  
                  return (
                    <div key={stat.id} className="relative group overflow-hidden bg-[var(--card)] border border-[var(--border)] p-5 rounded-[24px] shadow-xl hover:shadow-2xl transition-all duration-300">
                      {/* Hoverda canlanan arka plan */}
                      <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 opacity-[0.03] group-hover:opacity-[0.08] transition-all duration-500 transform rotate-12">
                        <Icon size={120} />
                      </div>
                      <div className="relative z-10">
                        <div className="flex items-center justify-between mb-4">
                          <div className="p-3 rounded-2xl bg-[var(--bg-alt)] border border-[var(--border)] shadow-inner" style={{ color: 'var(--primary)' }}>
                            <Icon size={22} />
                          </div>
                          <span className="text-[10px] font-black text-right uppercase opacity-40 tracking-widest" style={{ color: 'var(--text)' }}>{stat.label}</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-2xl font-black tracking-tighter mb-1" style={{ color: 'var(--text)' }}>
                            {isCurrency ? formatCurrency(stat.value) : stat.value}
                          </span>
                          <div className="flex items-center gap-1">
                             <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
                             <span className="text-[10px] font-bold opacity-40 uppercase" style={{ color: 'var(--text)' }}>Güncel Veri</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          }

          // --- 2. MODERN TABLE (GLASS EFFECT) ---
          if (widget.type === "table") {
            return (
              <div key={widget.id} className="col-span-12 lg:col-span-8 bg-[var(--card)] rounded-[28px] shadow-2xl border border-[var(--border)] overflow-hidden">
                <div className="p-6 border-b border-[var(--border)] flex items-center justify-between bg-[var(--bg-alt)]/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-[var(--bg)] border border-[var(--border)]" style={{ color: 'var(--primary)' }}>
                      {widget.icon && React.createElement(IconMap[widget.icon] || History, { size: 20 })}
                    </div>
                    <h2 className="font-black text-lg tracking-tight" style={{ color: 'var(--text)' }}>{widget.title}</h2>
                  </div>
                  <button className="text-[11px] font-black uppercase tracking-widest opacity-40 hover:opacity-100 transition-opacity" style={{ color: 'var(--text)' }}>Tümünü Gör</button>
                </div>
                <div className="p-2">
                  <DataTable value={widget.data} rows={8} className="p-datatable-modern custom-table" responsiveLayout="stack" breakpoint="960px">
                    <Column header="Tarih" body={(row) => <span className="opacity-50 text-[11px] font-bold" style={{ color: 'var(--text)' }}>{new Date(row.createdAt).toLocaleDateString('tr-TR')}</span>} />
                    <Column header="İşlem / Cari" body={(row) => <span className="font-black text-sm tracking-tight uppercase" style={{ color: 'var(--text)' }}>{row.accountName || row.orderNumber}</span>} />
                    <Column header="Durum" body={(row) => (
                      <div className="flex items-center gap-2">
                         <div className="w-2 h-2 rounded-full" style={{ backgroundColor: row.status.color }}></div>
                         <span className="text-[10px] font-black uppercase tracking-tighter" style={{ color: 'var(--text)' }}>{row.status.name}</span>
                      </div>
                    )} />
                    <Column header="Tutar" align="right" body={(row) => (
                      <span className={`font-black text-sm tracking-tighter ${row.totalAmount === null ? 'blur-[4px] opacity-20' : ''}`} style={{ color: 'var(--text)' }}>
                        {formatCurrency(row.totalAmount)}
                      </span>
                    )} />
                  </DataTable>
                </div>
              </div>
            );
          }

          // --- 3. ALERT LIST (CRITICAL STOCKS) ---
          if (widget.type === "list_alert") {
            return (
              <div key={widget.id} className="col-span-12 lg:col-span-4 bg-[var(--card)] rounded-[28px] shadow-2xl border border-[var(--border)] overflow-hidden">
                <div className="p-6 border-b border-[var(--border)] flex items-center gap-3" style={{ backgroundColor: 'rgba(239, 68, 68, 0.05)' }}>
                  <div className="p-2.5 rounded-xl bg-red-500 text-white shadow-lg shadow-red-500/20">
                    <AlertTriangle size={20} />
                  </div>
                  <h2 className="font-black text-lg tracking-tight" style={{ color: 'var(--text)' }}>{widget.title}</h2>
                </div>
                <div className="p-4 space-y-3 max-h-[500px] overflow-y-auto no-scrollbar">
                  {widget.data.length > 0 ? widget.data.map((item: any, idx: number) => (
                    <div key={idx} className="group relative flex justify-between items-center p-4 rounded-2xl bg-[var(--bg-alt)] border border-[var(--border)] hover:border-red-500/50 transition-all duration-300">
                      <div className="flex flex-col">
                        <span className="text-sm font-black tracking-tight" style={{ color: 'var(--text)' }}>{item.name}</span>
                        <span className="text-[10px] font-bold opacity-30 uppercase tracking-widest leading-none mt-1" style={{ color: 'var(--text)' }}>{item.sku || "N/A"}</span>
                      </div>
                      <div className={`flex flex-col items-end`}>
                        <span className={`text-sm font-black ${item.quantity <= 0 ? 'text-red-500 animate-pulse' : 'text-orange-500'}`}>
                          {item.quantity}
                        </span>
                        <span className="text-[8px] font-black opacity-40 uppercase" style={{ color: 'var(--text)' }}>Stok Adedi</span>
                      </div>
                    </div>
                  )) : (
                    <div className="flex flex-col items-center justify-center py-20 opacity-20">
                       <ShoppingBag size={40} className="mb-2" />
                       <span className="text-sm font-bold uppercase tracking-widest">Kritik Stok Yok</span>
                    </div>
                  )}
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>
      )}
    </div>
  );
};